import { useCallback, useEffect, useState } from 'react';

function loadJson<T>(storageKey: string): T | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(storageKey);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function saveJson(storageKey: string, value: unknown | null): void {
  try {
    if (value == null) {
      sessionStorage.removeItem(storageKey);
      return;
    }
    sessionStorage.setItem(storageKey, JSON.stringify(value));
  } catch {
    // quota / private mode
  }
}

/**
 * Persist list filter state in sessionStorage so Back from a record remount restores filters.
 * Pass `initial` when hydrating from URL query params (takes precedence on first mount).
 */
export function usePersistedListFilters<T>(
  storageKey: string,
  initial?: T | null,
): [T | null, (next: T | null) => void] {
  const [filters, setFiltersState] = useState<T | null>(() => {
    if (initial != null) return initial;
    return loadJson<T>(storageKey);
  });

  const setFilters = useCallback(
    (next: T | null) => {
      setFiltersState(next);
      saveJson(storageKey, next);
    },
    [storageKey],
  );

  useEffect(() => {
    saveJson(storageKey, filters);
  }, [filters, storageKey]);

  return [filters, setFilters];
}
